import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * LOBBERO STAYS — the host's side of the guest check-in (plan S6;
 * lobbero_be/src/stays/routes/{checkin,checkin-send}.js). The DEFAULT axios
 * instance like the other Stays clients (cookies, CSRF, refresh). Every
 * failure is a StaysApiError with the server's code and words.
 */

export type CheckinLinkStatus = 'none' | 'live' | 'completed' | 'revoked' | 'expired';

export interface CheckinCardGuest {
  guest_id: string;
  position: number;
  is_lead: boolean;
  first_name: string;
  last_name: string;
  band: 'adult' | 'minor_14_17' | 'minor_under_14';
  mode: 'vendor' | 'declared' | 'in_person' | 'not_required';
  status: string;
  /** IS0: the in-house ID check (null when none is recorded). Never a photo or a document value. */
  id_check?: CheckinIdCheck | null;
}

/** Codes of the automatic checks (lobbero_be src/stays/identity/inhouse-checks.js + A-6 in SQL). */
export type IdCheckCode = 'A-1' | 'A-2' | 'A-3' | 'A-4' | 'A-5' | 'A-6';

export interface CheckinIdCheck {
  method: 'ocr' | 'manual';
  review_state: 'auto_confirmed' | 'needs_review' | 'confirmed' | 'rejected';
  failed_checks: IdCheckCode[];
  checked_by_name: string | null;
  checked_at: string | null;
  label: string;
  can_confirm: boolean;
  can_undo: boolean;
  can_ask_rescan: boolean;
}

export interface CheckinCardEvent {
  id: string;
  kind: 'arrived' | 'checked_out' | 'help';
  at: string;
  note: string | null;
  acknowledged: boolean;
}

export interface CheckinCard {
  reservation_id: string;
  listing_id: string;
  lead_name: string;
  lead_first_name: string;
  has_email: boolean;
  check_in: string;
  check_out: string;
  status: string;
  party: { booked: number; adults: number | null; minors: number | null };
  party_mismatch: boolean;
  travel_purpose: string | null;
  link: {
    status: CheckinLinkStatus;
    reusable: boolean;
    sent_at: string | null;
    sent_via: 'email' | 'copy' | 'whatsapp' | null;
    last_delivery: { channel: string; outcome: string; at: string } | null;
    opened_count: number;
    last_opened_at: string | null;
    emails_last_hour: number;
    resend_available_at: string | null;
  };
  trip_confirmed_at: string | null;
  identity: {
    required: number;
    verified: number;
    declared: number;
    children: number;
    in_person: number;
    pending: number;
    failed: number;
    not_started: number;
    not_required: number;
    /** IS0: in-house IDs that need a look or were sent back. */
    needs_look?: number;
    guests: CheckinCardGuest[];
  };
  rules: {
    required: boolean;
    signed_at: string | null;
    version: number | null;
    kind: 'typed' | 'drawn' | null;
    signature_id: string | null;
    emailed_guest_at: string | null;
    emailed_host_at: string | null;
    mail_failed: boolean;
  };
  payment: { required: boolean; done_at: string | null; label: string };
  completed_at: string | null;
  access_revoked: boolean;
  events: CheckinCardEvent[];
  tz: string;
}

export interface CopyPayload {
  url: string;
  message_text: string;
  whatsapp_url: string;
  rotated: boolean;
}

export interface HouseRulesVersion {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'superseded';
  summary_en: string;
  body_en: string;
  body_sha256: string | null;
  published_at: string | null;
  superseded_at: string | null;
  updated_at: string | null;
}

export interface HouseRulesState {
  published: HouseRulesVersion | null;
  draft: HouseRulesVersion | null;
  history: { version: number; status: string; published_at: string | null; superseded_at: string | null }[];
  signed_count: number;
}

export interface CheckinSettings {
  auto_send_link: boolean;
  host_display_name: string;
  purpose_required: boolean;
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const listing = (id: string) => `${apiRoute}/stays/listings/${encodeURIComponent(id)}`;
const stay = (id: string) => `${apiRoute}/stays/reservations/${encodeURIComponent(id)}`;
const send = (id: string) => `${apiRoute}/stays/checkin-send/${encodeURIComponent(id)}`;

export const listCheckinCards = (listingId: string, state: 'upcoming' | 'all' = 'upcoming'): Promise<{ cards: CheckinCard[] }> =>
  call(axios.get(`${listing(listingId)}/checkin-cards`, { params: { state } }), 'Could not load the check-ins.');

export const getCheckinCard = (reservationId: string): Promise<CheckinCard> =>
  call(axios.get(`${stay(reservationId)}/checkin-card`), 'Could not load this check-in.');

export const sendCheckinLink = (
  reservationId: string,
  resend = false
): Promise<{ delivery: { outcome: string; rotated: boolean }; card: CheckinCard }> =>
  call(axios.post(`${send(reservationId)}/${resend ? 'resend' : 'send'}`, { channel: 'email' }), 'Could not send the link.');

export const copyCheckinLink = (reservationId: string): Promise<CopyPayload> =>
  call(axios.post(`${stay(reservationId)}/checkin-link/copy`, {}), 'Could not get the link.');

export const markCheckinLinkCopied = (reservationId: string, channel: 'copy' | 'whatsapp'): Promise<void> =>
  call(axios.post(`${stay(reservationId)}/checkin-link/copied`, { channel }), 'Could not record that.').then(() => undefined);

export const revokeCheckinLink = (reservationId: string): Promise<CheckinCard> =>
  call(axios.post(`${stay(reservationId)}/checkin-link/revoke`, {}), 'Could not stop the link.');

export const reissueCheckinLink = (
  reservationId: string
): Promise<{ card: CheckinCard; link: { url: string; message_text: string; whatsapp_url: string } }> =>
  call(axios.post(`${stay(reservationId)}/checkin-link/reissue`, {}), 'Could not make a new link.');

export const verifyGuestInPerson = (guestId: string, note?: string): Promise<CheckinCard> =>
  call(
    axios.post(`${apiRoute}/stays/guests/${encodeURIComponent(guestId)}/verify-in-person`, note ? { note } : {}),
    'Could not record the check.'
  );

/** IS0: "Mark as checked" / undo / "Ask to scan again" on an in-house ID. Each answers the fresh card. */
const idCheckUrl = (guestId: string, what: 'check' | 'rescan') =>
  `${apiRoute}/stays/identity/guests/${encodeURIComponent(guestId)}/${what}`;

export const markIdChecked = (guestId: string): Promise<CheckinCard> =>
  call(axios.post(idCheckUrl(guestId, 'check'), {}), 'Could not mark the ID as checked.');

export const undoIdChecked = (guestId: string): Promise<CheckinCard> =>
  call(axios.delete(idCheckUrl(guestId, 'check')), 'Could not undo the check.');

export const askIdRescan = (guestId: string): Promise<CheckinCard> =>
  call(axios.post(idCheckUrl(guestId, 'rescan'), {}), 'Could not ask for a new photo.');

export const acknowledgeGuestEvent = (eventId: string): Promise<void> =>
  call(axios.post(`${apiRoute}/stays/guest-events/${encodeURIComponent(eventId)}/acknowledge`, {}), 'Could not mark it as seen.')
    .then(() => undefined);

export const signedRulesPdfUrl = (signatureId: string): Promise<{ url: string }> =>
  call(axios.get(`${apiRoute}/stays/signatures/${encodeURIComponent(signatureId)}/pdf`), 'Could not open the signed copy.');

export const getHouseRules = (listingId: string): Promise<HouseRulesState> =>
  call(axios.get(`${listing(listingId)}/house-rules`), 'Could not load the house rules.');

export const saveHouseRulesDraft = (
  listingId: string,
  input: { summary_en: string | null; body_en: string | null }
): Promise<{ draft: HouseRulesVersion }> =>
  call(axios.put(`${listing(listingId)}/house-rules/draft`, input), 'Could not save the draft.');

export const publishHouseRules = (listingId: string): Promise<{ published: HouseRulesVersion }> =>
  call(axios.post(`${listing(listingId)}/house-rules/publish`, {}), 'Could not publish the house rules.');

export const discardHouseRulesDraft = (listingId: string): Promise<void> =>
  call(axios.delete(`${listing(listingId)}/house-rules/draft`), 'Could not discard the draft.').then(() => undefined);

export const getCheckinSettings = (listingId: string): Promise<CheckinSettings> =>
  call(axios.get(`${listing(listingId)}/checkin-settings`), 'Could not load the check-in settings.');

export const saveCheckinSettings = (listingId: string, input: Partial<CheckinSettings>): Promise<CheckinSettings> =>
  call(axios.put(`${listing(listingId)}/checkin-settings`, input), 'Could not save the check-in settings.');
